import { personRecord } from "./person-record";

/**
 * A walkthrough already in progress, carried across the relaunch macOS forces.
 *
 * Granting Screen Recording makes macOS ask the hire to quit and reopen the app, so the common
 * success path ends in a fresh load. Held in memory only, that load sees both grants already held,
 * reads the hire as having arrived granted, and never records the conversion the screen exists for --
 * along with the System Settings trips it took them.
 */

/**
 * How long a stored attempt is still believed.
 *
 * Long enough to cover a quit, a relaunch and the hire finding the app again; short enough that
 * tomorrow's session is not credited with today's, which would inflate the numerator.
 */
const ATTEMPT_TTL_MS = 60 * 60 * 1000;

/** An attempt the hire had underway when the app last closed. */
export interface GrantAttempt {
  /** Trips to System Settings so far, so the resolution reports the real attempt count. */
  readonly trips: number;
}

const record = personRecord<GrantAttempt>(
  "macGrantAttempt",
  ATTEMPT_TTL_MS,
  (value): value is GrantAttempt =>
    typeof (value as GrantAttempt | undefined)?.trips === "number"
);

/**
 * Record that this hire is partway through the walkthrough.
 *
 * @param personId The hire working through it, or undefined before one is resolved.
 * @param trips Trips to System Settings so far.
 */
export const markGrantAttempt = (
  personId: string | undefined,
  trips: number
): void => record.write(personId, { trips });

/** Forget the attempt, once it has been counted or abandoned. */
export const clearGrantAttempt = (): void => record.clear();

/**
 * The attempt this hire had underway, if one is recorded and still recent.
 *
 * @param personId The hire asking, or undefined before one is resolved.
 */
export const readGrantAttempt = (
  personId: string | undefined
): GrantAttempt | undefined => record.read(personId);
